// Werbestrategie-Analyse je Projekt — siehe WERBESTRATEGIE-KONZEPT.md.
//
// Ersetzt die ein bis zwei Tage Vorrecherche vor jedem Neukunden. Das Ergebnis
// ist bewusst schmal: was allgemein an der Website fehlt, und zwei bis drei
// Kampagnen mit je einer Landingpage und zwei bis drei Angles. Je Block ein
// eigener Auftrag für Claude Code, je Kampagne der Knopf in den Google-Ads-
// Bereich — dort entstehen Keywords, Gebote und Anzeigentexte.
//
// Die Analyse läuft in Stufen (Crawl → Markt → Suchergebnisse → Nachfrage →
// Einordnung → Plan → Aufträge), weil sie zusammen Minuten braucht. Diese
// Komponente ruft eine Stufe nach der anderen ab und zeigt solange, woran
// gerade gearbeitet wird.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Compass, Loader2, AlertTriangle, RotateCcw, Mail, Plus, X, Trash2, Ban, Terminal } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addLandingpage,
  getStrategie,
  getStrategieBriefing,
  startStrategie,
  strategieAbbrechen,
  strategieVerwerfen,
  strategieWeiter,
  strategieSaeuleUebergeben,
  strategieStufen,
  type StrategieAnalyse,
} from '@/lib/analytics-api';

const KLASSE: Record<string, { label: string; cls: string }> = {
  ware: { label: 'Ware — Handel gewinnt', cls: 'bg-red-50 text-red-700 border-red-200' },
  handwerker: { label: 'Handwerker gesucht', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  situation: { label: 'Lebenssituation', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  orientierung: { label: 'Orientierung — Blog', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const EIGNUNG: Record<string, string> = {
  gut: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  bedingt: 'bg-amber-50 text-amber-700 border-amber-200',
  ungeeignet: 'bg-gray-100 text-gray-500 border-gray-200',
};

/**
 * Welcher Art eine Kampagne ist.
 *
 * „grund" gibt es immer — jemand sucht schlicht einen Betrieb dieses Gewerks in
 * seiner Nähe. Was DARÜBER liegt, hängt am Gewerk: Bei dem einen werden die
 * Aufträge direkt benannt („dachsanierung"), beim anderen kommt die Nachfrage
 * als Lebenssituation („bad altersgerecht umbauen"). Das entscheidet die
 * Analyse je Kunde, nicht ein festes Muster.
 */
const KAMPAGNENTYP: Record<string, { label: string; cls: string; hilfe: string }> = {
  grund: {
    label: 'Grundkampagne',
    cls: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    hilfe: 'Gewerk + Ort — die Kampagne, die ganzjährig trägt. Die anderen kommen obendrauf.',
  },
  leistung: {
    label: 'Leistung',
    cls: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    hilfe: 'Eine eigenständige Leistung, die direkt gesucht wird.',
  },
  situation: {
    label: 'Lebenssituation',
    cls: 'border-blue-200 bg-blue-50 text-blue-700',
    hilfe: 'Eine Lebenssituation, in der die Leistung erst die Antwort ist — meist wenig umkämpft.',
  },
};

/**
 * Wie groß der Auftrag hinter einer Nachfrage typischerweise ist.
 *
 * Neben der Absicht die zweite Frage — und die härtere: Ein Klick kostet
 * dasselbe, ob am Ende eine Arbeitsplatte oder ein kompletter Innenausbau
 * herauskommt. „Küche anpassen" war sauber als Handwerkersuche eingeordnet und
 * trotzdem ein Verlustgeschäft.
 */
const AUFTRAGSWERT: Record<string, string> = {
  gross: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  mittel: 'border-gray-200 bg-gray-50 text-gray-600',
  klein: 'border-red-200 bg-red-50 text-red-700',
};

/**
 * Eine Themensäule als Text, Feld für Feld in PUBLIVOs Benennung.
 *
 * Solange die direkte Übergabe nicht verdrahtet ist, ist das der Weg: kopieren
 * und in PUBLIVO einsetzen. Die Feldnamen stehen bewusst mit dabei, damit
 * nichts in der falschen Spalte landet.
 */
function saeuleAlsText(t: NonNullable<StrategieAnalyse['plan']>['publivo_themen'] extends (infer U)[] | undefined ? U : never): string {
  const z: string[] = [`Name: ${t.name}`];
  if (t.description) z.push('', `description:\n${t.description}`);
  if (t.blog_description) z.push('', `blog_description:\n${t.blog_description}`);
  if (t.research_keywords?.length) z.push('', `research_keywords:\n${t.research_keywords.join(', ')}`);
  if (t.example_topics?.length) z.push('', 'example_topics:', ...t.example_topics.map((e) => `- ${e}`));
  if (t.warum_kein_ads) z.push('', `Warum organisch statt Anzeigen: ${t.warum_kein_ads}`);
  return z.join('\n');
}

/** Abgleich der vorgeschlagenen Säule mit dem PUBLIVO-Bestand der Organisation. */
const SAEULEN_ABGLEICH: Record<string, { label: string; cls: string }> = {
  neu: { label: 'neu', cls: 'border-violet-200 bg-violet-50 text-violet-700' },
  nachschaerfen: { label: 'bestehende Säule nachschärfen', cls: 'border-amber-200 bg-amber-50 text-amber-700' },
  deckt_sich: { label: 'existiert bereits', cls: 'border-gray-200 bg-gray-50 text-gray-500' },
};

const DRINGLICH: Record<string, string> = {
  hoch: 'border-red-200 bg-red-50 text-red-700',
  mittel: 'border-amber-200 bg-amber-50 text-amber-700',
  niedrig: 'border-gray-200 bg-gray-50 text-gray-500',
};

export default function StrategiePanel({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [ort, setOrt] = useState('');
  const [umkreis, setUmkreis] = useState('40');
  // Bundesweit-Projekte (z. B. Nicos Eigenmarketing): ganz Deutschland statt
  // Ort + Umkreis — die Geo-Felder entfallen dann.
  const [bundesweit, setBundesweit] = useState(false);
  const [url, setUrl] = useState('');
  // Nur nötig, solange die Website hinter /zugang steht. Wird am Projekt
  // gespeichert, damit auch Angle-Analyse und Kampagnen-Generator hineinkommen.
  const [sitePw, setSitePw] = useState('');
  // Welche Leistungen der Betrieb WIRKLICH in den Mittelpunkt stellt. Ohne die
  // Vorgabe leitet die Analyse aus dem Gewerk das typische Muster ab — bei
  // KLENKE (Tischlerei) schlug sie Schränke und Küchen vor, obwohl der Betrieb
  // Fenster und Türen fertigt. Das weiß nur Nico, nicht der Crawl.
  //
  // Als GETRENNTE Felder statt Komma-Konvention: Der Begriff geht in die
  // Google-Saat, die Zielgruppe nur an die Auswertung. So kann beim Eintippen
  // nichts falsch getrennt werden — Kommas sind überall erlaubt.
  const [fokusZeilen, setFokusZeilen] = useState<Array<{ leistung: string; zielgruppe: string }>>([
    { leistung: '', zielgruppe: '' },
  ]);
  const fokusSetzen = (i: number, feld: 'leistung' | 'zielgruppe', wert: string) =>
    setFokusZeilen((z) => z.map((r, n) => (n === i ? { ...r, [feld]: wert } : r)));
  /**
   * WELCHE Stufe gerade angefragt ist — nicht bloß ob überhaupt eine läuft.
   *
   * Vorher stand hier ein einfaches Ja/Nein, das erst im `finally` zurückgesetzt
   * wurde. Läuft React sein Rendering früher als dieses `finally` — und das ist
   * ein Wettlauf, kein fester Ablauf —, sieht der Effekt für die NEUE Stufe noch
   * „läuft schon" und steigt aus. Danach ändert sich keine Abhängigkeit mehr:
   * Der Lauf steht still, ohne Fehler, ohne Anfrage. Genau so hing HOLZZAUBER
   * zehn Minuten auf „Suchergebnisse prüfen", während der Server gar nichts
   * gefragt wurde.
   *
   * Mit dem Stufennamen kann ein stehengebliebener Wert die nächste Stufe nicht
   * mehr blockieren — sie heißt anders.
   */
  const laufendeStufe = useRef<string | null>(null);
  const [stufeFehler, setStufeFehler] = useState<string | null>(null);
  // Merkt sich, dass abgebrochen wurde — sonst würde das Ergebnis der noch
  // laufenden Anfrage den Abbruch wieder überschreiben.
  const abgebrochen = useRef(false);
  const [seit, setSeit] = useState<number | null>(null);

  const key = ['strategie', projectId];
  const { data } = useQuery({ queryKey: key, queryFn: () => getStrategie(projectId) });
  const analyse = data?.analyse || null;
  const setzen = (a: StrategieAnalyse) => queryClient.setQueryData(key, { analyse: a });

  /**
   * Formular mit den Vorgaben des letzten Laufs vorbelegen.
   *
   * Wer neu startet, will fast immer dieselben Vorgaben — nur nachgeschärft.
   * Vorher begann das Formular leer, und Ort, Adresse und Fokus mussten bei
   * jedem Lauf neu getippt werden. Es füllt nur einmal beim Laden; was Nico
   * danach ändert, bleibt seins.
   */
  const vorbelegt = useRef(false);
  useEffect(() => {
    if (vorbelegt.current || !analyse?.params) return;
    vorbelegt.current = true;
    const p = analyse.params;
    if (p.geo_modus === 'bundesweit') setBundesweit(true);
    else if (p.ort) setOrt(p.ort);
    if (p.umkreis_km) setUmkreis(String(p.umkreis_km));
    if (p.website_url) setUrl(p.website_url);
    const f = p.fokus;
    const zeilen = Array.isArray(f)
      ? f.map((e) => ({ leistung: e.leistung || '', zielgruppe: e.praezisierung || '' }))
      : typeof f === 'string' && f.trim()
        ? f.split(/,(?![^()]*\))/).map((t) => {
            const m = t.trim().match(/^(.*?)\s*\(([^)]*)\)\s*$/);
            return m
              ? { leistung: m[1].trim(), zielgruppe: m[2].trim() }
              : { leistung: t.trim(), zielgruppe: '' };
          })
        : [];
    const gefuellt = zeilen.filter((z) => z.leistung);
    if (gefuellt.length) setFokusZeilen(gefuellt);
  }, [analyse]); // eslint-disable-line react-hooks/exhaustive-deps

  const startMut = useMutation({
    mutationFn: () => startStrategie(projectId, { ort: ort.trim(), umkreis_km: parseInt(umkreis) || 40, ...(bundesweit ? { geo_modus: 'bundesweit' as const } : {}), website_url: url.trim() || undefined, site_passwort: sitePw.trim() || undefined, fokus: (() => {
      const liste = fokusZeilen
        .map((z) => ({ leistung: z.leistung.trim(), ...(z.zielgruppe.trim() ? { praezisierung: z.zielgruppe.trim() } : {}) }))
        .filter((z) => z.leistung)
        .slice(0, 8);
      return liste.length ? liste : undefined;
    })() }),
    onSuccess: (d) => setzen(d.analyse),
    onError: (e: any) => toast.error(e.message),
  });

  // Seite an den Google-Ads-Tab übergeben: schreibt sie in die beworbenen
  // Landingpages, wo der Kampagnen-Generator sie danach vorfindet.
  const [uebernommen, setUebernommen] = useState<Record<string, boolean>>({});
  const uebernehmenMut = useMutation({
    mutationFn: (u: string) => addLandingpage(projectId, u),
    onSuccess: (_d, u) => {
      setUebernommen((v) => ({ ...v, [u]: true }));
      queryClient.invalidateQueries({ queryKey: ['landingpages', projectId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-lps', projectId] });
      toast.success('Als beworbene Landingpage übernommen');
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Themensäule nach PUBLIVO übergeben. Eine NEUE Säule ist die markierte
  // Ausnahme — dafür fragt der Server erst zurück, statt sie einfach anzulegen.
  const [saeuleStatus, setSaeuleStatus] = useState<Record<string, 'aktualisiert' | 'angelegt'>>({});
  const [saeuleLaeuft, setSaeuleLaeuft] = useState<string | null>(null);
  const saeuleUebergeben = async (name: string, confirmNew = false) => {
    setSaeuleLaeuft(name);
    try {
      const d = await strategieSaeuleUebergeben(projectId, analyse!.id, name, confirmNew);
      if (d.braucht_bestaetigung) {
        const liste = (d.vorhandene || []).join('\n· ');
        const weiter = window.confirm(
          `In PUBLIVO gibt es noch keine Säule „${name}".\n\n` +
          (liste ? `Vorhandene Säulen:\n· ${liste}\n\n` : 'Dort liegt bisher keine einzige Säule.\n\n') +
          'Neue Säule anlegen?'
        );
        if (weiter) await saeuleUebergeben(name, true);
        return;
      }
      if (d.ergebnis) {
        setSaeuleStatus((v) => ({ ...v, [name]: d.ergebnis! }));
        toast.success(d.ergebnis === 'angelegt' ? 'Säule in PUBLIVO angelegt' : 'Säule in PUBLIVO nachgeschärft');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Übergabe nach PUBLIVO fehlgeschlagen');
    } finally {
      setSaeuleLaeuft(null);
    }
  };

  // Briefing für den Kunden zur Website
  const [briefing, setBriefing] = useState<string | null>(null);
  const [briefingKopiert, setBriefingKopiert] = useState(false);
  const briefingMut = useMutation({
    mutationFn: (neu: boolean) => getStrategieBriefing(projectId, analyse!.id, neu),
    onSuccess: (d) => { setBriefing(d.briefing); setBriefingKopiert(false); },
    onError: (e: any) => toast.error(e.message),
  });

  const abbrechenMut = useMutation({
    mutationFn: () => strategieAbbrechen(projectId, analyse!.id, strategieStufen[analyse!.stufe] || analyse!.stufe),
    onSuccess: (d) => {
      abgebrochen.current = true;
      laufendeStufe.current = null;
      setStufeFehler(null);
      if (d.analyse) setzen(d.analyse);
      toast.success('Analyse angehalten — die fertigen Stufen bleiben erhalten');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const verwerfenMut = useMutation({
    mutationFn: () => strategieVerwerfen(projectId, analyse!.id),
    onSuccess: () => {
      abgebrochen.current = true;
      laufendeStufe.current = null;
      setStufeFehler(null);
      queryClient.setQueryData(key, { analyse: null });
      toast.success('Analyse verworfen');
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Stufen nacheinander abarbeiten. Bewusst sequenziell und mit Wächter gegen
  // Doppelläufe — jede Stufe kostet echtes Geld bei Apify und Perplexity.
  const stufeRechnen = (erzwingen = false) => {
    if (!analyse) return;
    // Nur DIESE Stufe darf nicht doppelt laufen. „Erneut versuchen" umgeht den
    // Wächter, weil dort dieselbe Stufe absichtlich noch einmal drankommt.
    if (!erzwingen && laufendeStufe.current === analyse.stufe) return;
    laufendeStufe.current = analyse.stufe;
    abgebrochen.current = false;
    setStufeFehler(null);
    setSeit(Date.now());
    strategieWeiter(projectId, analyse.id)
      .then((d) => { if (!abgebrochen.current) setzen(d.analyse); })
      .catch(async (e: any) => {
        // Läuft eine Stufe in Vercels Zeitlimit, schreibt der Server nichts —
        // der Datensatz bleibt auf „läuft". Ohne diesen Zustand würde sich der
        // Fortschritt endlos drehen, ohne dass etwas passiert.
        laufendeStufe.current = null;
        if (abgebrochen.current) return;

        // ERST NACHSEHEN, DANN MECKERN. Eine Stufe kann fertig gerechnet und
        // weggeschrieben sein, während nur die ANTWORT verlorengeht — bei
        // HOLZZAUBER kam ein HTTP 500, zwei Sekunden bevor der Server den
        // fertigen Plan sauber in die Datenbank schrieb. Ein Fehler an dieser
        // Stelle wäre schlicht falsch gewesen: Es war nichts kaputt.
        try {
          const frisch = await getStrategie(projectId);
          if (frisch.analyse && frisch.analyse.stufe !== analyse.stufe) {
            setzen(frisch.analyse);
            return;
          }
        } catch { /* dann bleibt es beim Fehler unten */ }

        setStufeFehler(e?.message || 'Die Stufe wurde abgebrochen (vermutlich Zeitlimit).');
      })
      .finally(() => { setSeit(null); });
  };

  useEffect(() => {
    if (!analyse || analyse.status !== 'laeuft' || stufeFehler) return;
    stufeRechnen();
  }, [analyse?.id, analyse?.stufe, analyse?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Aufpasser gegen stillstehende Läufe.
   *
   * Der Effekt oben hängt an Zustandsänderungen. Bleibt eine aus — durch einen
   * Wettlauf, einen verschluckten Fehler, einen Tabwechsel —, steht der Lauf
   * still und niemand merkt es: Der Fortschritt dreht sich weiter, aber es
   * geht keine Anfrage mehr raus. Genau das ist passiert.
   *
   * Diese Uhr prüft alle fünf Sekunden das Gegenteil: Der Datensatz sagt
   * „läuft", aber es ist KEINE Anfrage unterwegs (`seit` ist leer). Dann fehlt
   * ein Anstoß — und sie gibt ihn. Doppelbuchungen sind ausgeschlossen, weil
   * eine laufende Anfrage `seit` gesetzt hat.
   */
  useEffect(() => {
    if (!analyse || analyse.status !== 'laeuft' || stufeFehler) return;
    const uhr = setInterval(() => {
      if (seit === null) stufeRechnen(true);
    }, 5000);
    return () => clearInterval(uhr);
  }, [analyse?.id, analyse?.stufe, analyse?.status, seit, stufeFehler]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = analyse?.plan || null;
  const stufenListe = ['bestand', 'recherche', 'serp', 'nachfrage', 'einordnung', 'plan', 'auftrag'];
  const aktuellerIndex = analyse ? stufenListe.indexOf(analyse.stufe) : -1;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Compass className="h-4 w-4 text-indigo-600" /> Werbestrategie
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Liest die Website, recherchiert Markt und Wettbewerber, prüft echte Suchergebnisse und misst die Nachfrage —
          und legt daraus fest, welche zwei bis drei Leistungen beworben werden, auf welcher Seite und mit welchen Angles.
          Keywords, Anzeigentexte und Kampagnen macht danach der Google-Ads-Bereich.
        </p>
      </div>

      {/* ── Start ── */}
      {(!analyse || analyse.status === 'fertig' || analyse.status === 'fehler') && (
        <div className="flex flex-wrap items-end gap-2 border-b border-gray-100 bg-gray-50/60 px-4 py-3">
          <div>
            <label className="mb-0.5 block text-[10px] text-gray-400">Zielgebiet</label>
            <label className="inline-flex cursor-pointer select-none items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm">
              <input type="checkbox" checked={bundesweit} onChange={(e) => setBundesweit(e.target.checked)} className="rounded border-gray-300" />
              Bundesweit
            </label>
          </div>
          {!bundesweit && (
            <>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">Ort</label>
                <input value={ort} onChange={(e) => setOrt(e.target.value)} placeholder="z. B. Kassel"
                  className="w-36 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="mb-0.5 block text-[10px] text-gray-400">Umkreis km</label>
                <input value={umkreis} onChange={(e) => setUmkreis(e.target.value)} inputMode="numeric"
                  className="w-16 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
              </div>
            </>
          )}
          <div className="min-w-0 flex-1">
            <label className="mb-0.5 block text-[10px] text-gray-400">Website (leer = aus dem Projekt)</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
              className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="mb-0.5 block text-[10px] text-gray-400">Website-Passwort</label>
            <input value={sitePw} onChange={(e) => setSitePw(e.target.value)} type="password" placeholder="falls noch gesperrt"
              autoComplete="off"
              className="w-40 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="min-w-0 flex-1 basis-full space-y-1.5">
            <label className="block text-[10px] text-gray-400">
              Leistungen im Fokus — die Analyse baut Kampagnen NUR dafür. Leer lassen = sie entscheidet selbst.
            </label>
            {fokusZeilen.map((z, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5">
                <input
                  value={z.leistung}
                  onChange={(e) => fokusSetzen(i, 'leistung', e.target.value)}
                  placeholder="Leistung / Intention — z. B. Fenster tauschen"
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                />
                <input
                  value={z.zielgruppe}
                  onChange={(e) => fokusSetzen(i, 'zielgruppe', e.target.value)}
                  placeholder="Für wen? (optional) — z. B. Einfamilienhaus, Wechsel zu Kunststofffenstern"
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                />
                {fokusZeilen.length > 1 && (
                  <button
                    onClick={() => setFokusZeilen((zn) => zn.filter((_, n) => n !== i))}
                    aria-label="Zeile entfernen"
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {fokusZeilen.length < 8 && (
              <button
                onClick={() => setFokusZeilen((z) => [...z, { leistung: '', zielgruppe: '' }])}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800"
              >
                <Plus className="h-3 w-3" /> Leistung hinzufügen
              </button>
            )}
          </div>
          <button
            onClick={() => { if (!bundesweit && !ort.trim()) { toast.error('Bitte Ort angeben'); return; } startMut.mutate(); }}
            disabled={startMut.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {startMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />}
            {analyse ? 'Neue Analyse' : 'Analyse starten'}
          </button>
        </div>
      )}

      {/* ── Fortschritt ── */}
      {analyse && analyse.status === 'laeuft' && (
        <div className="border-b border-gray-100 px-4 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {stufenListe.map((st, i) => (
              <span key={st}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium ${
                  i < aktuellerIndex ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : i === aktuellerIndex ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-gray-50 text-gray-400'
                }`}>
                {i < aktuellerIndex ? <Check className="h-3 w-3" /> : i === aktuellerIndex ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                {strategieStufen[st]}
              </span>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="text-[11px] text-gray-400">
              Das dauert einige Minuten — Wettbewerber und Suchergebnisse werden wirklich abgerufen. Die Seite kann offen bleiben.
              {seit && <span className="ml-1">Diese Stufe läuft seit {Math.round((Date.now() - seit) / 1000)} s.</span>}
            </p>
            <button
              onClick={() => abbrechenMut.mutate()}
              disabled={abbrechenMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60"
            >
              {abbrechenMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
              Anhalten
            </button>
          </div>
        </div>
      )}

      {stufeFehler && analyse?.status === 'laeuft' && (
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Die Stufe „{strategieStufen[analyse.stufe]}" wurde abgebrochen. Alles Vorherige ist gespeichert —
              du kannst genau hier weitermachen. ({stufeFehler})
            </span>
          </p>
          <button
            onClick={() => stufeRechnen(true)}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Stufe erneut versuchen
          </button>
        </div>
      )}

      {analyse?.status === 'fehler' && (
        <div className="border-b border-gray-100 px-4 py-3">
          <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{analyse.fehler}</span>
          </p>
          <button
            onClick={() => { setStufeFehler(null); setzen({ ...analyse, status: 'laeuft' }); }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Ab dieser Stufe fortsetzen
          </button>
          <button
            onClick={() => { if (window.confirm('Diese Analyse ganz verwerfen? Alle Zwischenergebnisse gehen verloren.')) verwerfenMut.mutate(); }}
            disabled={verwerfenMut.isPending}
            className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
          >
            <Trash2 className="h-3.5 w-3.5" /> Verwerfen
          </button>
        </div>
      )}

      {/* ── Ergebnis ──
          Aufbau folgt dem Arbeitsweg: erst was allgemein an der Website zu tun
          ist, dann Kampagne für Kampagne. Je Block ein Auftrag für Claude Code,
          je Kampagne der Knopf in den Google-Ads-Bereich. Belege liegen
          zugeklappt darunter — sie begründen die Entscheidung, sind aber nicht
          die Entscheidung. */}
      {plan && (
        <div className="space-y-5 px-4 py-4">
          {analyse?.params?.website_url?.includes('.vercel.app') && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Analysiert wurde eine Vorschau-Adresse (<code className="text-[11px]">.vercel.app</code>). Für die Analyse ist das
              in Ordnung — aber übernommene Landingpages tragen dann diese Adresse, und Anzeigen würden dorthin zeigen.
              Nach dem Go-live die Seiten mit der echten Domain neu übernehmen.
            </p>
          )}

          {plan.zusammenfassung && (
            <p className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 text-sm leading-relaxed text-gray-800">
              {plan.zusammenfassung}
            </p>
          )}

          {/* ── 1. Was allgemein an der Website zu tun ist ── */}
          {(plan.website_befund?.luecken?.length || plan.website_auftrag) && (
            <div className="rounded-lg border border-gray-200">
              <div className="border-b border-gray-100 px-3 py-2">
                <p className="text-xs font-semibold text-gray-900">Allgemein an der Website</p>
                {plan.website_befund?.geprueft && (
                  <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500">{plan.website_befund.geprueft}</p>
                )}
              </div>
              <div className="space-y-2 px-3 py-2.5">
                {(plan.website_befund?.luecken || []).map((l, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-medium text-gray-900">{l.was}</span>
                    {l.dringlichkeit && (
                      <span className={`ml-1.5 rounded border px-1 py-0.5 text-[10px] ${DRINGLICH[l.dringlichkeit] || 'border-gray-200 text-gray-500'}`}>
                        {l.dringlichkeit}
                      </span>
                    )}
                    {l.warum && <p className="mt-0.5 text-[11px] text-gray-600">{l.warum}</p>}
                  </div>
                ))}
                {plan.website_auftrag && (
                  <PromptKnopf
                    text={plan.website_auftrag}
                    titel="Auftrag für Claude Code — Website allgemein"
                    beschriftung="Auftrag für Claude Code"
                  />
                )}
              </div>
            </div>
          )}

          {/* ── 2. Die Kampagnen ── */}
          {!!plan.kampagnen?.length && (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                Diese {plan.kampagnen.length === 1 ? 'Kampagne' : `${plan.kampagnen.length} Kampagnen`} empfehle ich
              </p>
              <div className="space-y-3">
                {plan.kampagnen.map((k, i) => {
                  const lp = k.landingpage || {};
                  const zuBauen = lp.status === 'zu_bauen';
                  const auftrag = (plan.kampagnen_auftraege || []).find((a) => a.kampagne === k.name)?.auftrag;
                  return (
                    <div key={i} className="rounded-lg border border-gray-200">
                      <div className="border-b border-gray-100 px-3 py-2.5">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-sm font-semibold text-gray-900">{k.name}</span>
                          {k.typ && KAMPAGNENTYP[k.typ] && (
                            <span
                              className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${KAMPAGNENTYP[k.typ].cls}`}
                              title={KAMPAGNENTYP[k.typ].hilfe}
                            >
                              {KAMPAGNENTYP[k.typ].label}
                            </span>
                          )}
                          {k.nachfrage && <span className="text-[11px] text-gray-500">{k.nachfrage}</span>}
                        </div>
                        {k.warum && <p className="mt-0.5 text-xs text-gray-700">{k.warum}</p>}
                        {k.auftrag_typisch && (
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            <span className="text-gray-400">Typischer Auftrag: </span>{k.auftrag_typisch}
                          </p>
                        )}

                        {!!k.angles?.length && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {k.angles.map((a, j) => (
                              <span
                                key={j}
                                className="inline-flex items-baseline gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-800"
                                title={[a.suchintention, a.url ? `Ziel: ${a.url}` : 'Ziel: ?angle=-Variante der Landingpage'].filter(Boolean).join(' — ')}
                              >
                                {a.url ? '↗ ' : ''}{a.name}
                                {a.schluessel && <code className="text-[10px] text-blue-500">{a.schluessel}</code>}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                              zuBauen ? 'border-amber-200 bg-amber-50 text-amber-700' : EIGNUNG[lp.eignung || 'gut']
                            }`}
                          >
                            {zuBauen ? 'Seite fehlt noch' : `Seite vorhanden · ${lp.eignung || 'gut'}`}
                          </span>
                          {lp.url && <span className="min-w-0 flex-1 truncate text-[11px] text-gray-500">{lp.url}</span>}
                        </div>
                        {lp.begruendung && <p className="text-xs text-gray-700">{lp.begruendung}</p>}

                        {!!lp.anpassungen?.length && (
                          <ul className="space-y-0.5">
                            {lp.anpassungen.map((f, j) => (
                              <li key={j} className="text-[11px] text-amber-800">• {f}</li>
                            ))}
                          </ul>
                        )}

                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                          {auftrag && (
                            <PromptKnopf
                              text={auftrag}
                              titel={`Auftrag für Claude Code — ${k.name}`}
                              beschriftung="Auftrag für diese Seite"
                            />
                          )}
                          {/* Übergabe an den Google-Ads-Bereich. Bei einer Seite,
                              die es noch nicht gibt, wäre das eine tote Adresse —
                              deshalb erst nach dem Bauen. */}
                          {!zuBauen && lp.url && (
                            <button
                              onClick={() => uebernehmenMut.mutate(lp.url!)}
                              disabled={uebernehmenMut.isPending || uebernommen[lp.url]}
                              title="Als beworbene Landingpage übernehmen — danach steht sie im Google-Ads-Bereich bereit"
                              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-60 ${
                                uebernommen[lp.url]
                                  ? 'border border-emerald-300 text-emerald-700'
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
                              }`}
                            >
                              {uebernommen[lp.url] ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                              {uebernommen[lp.url] ? 'In Google Ads übernommen' : 'Für Google Ads übernehmen'}
                            </button>
                          )}
                          {zuBauen && (
                            <span className="text-[11px] text-gray-400">
                              Nach dem Bauen hier übernehmen — dann steht die Seite im Google-Ads-Bereich.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── 3. Was organisch läuft statt über Anzeigen ──
              Nicht der Resterampe-Block: Hier landet, was nachweislich gefragt
              ist, aber kein messbares Suchvolumen hat. Ohne Suchanfragen gibt
              es keine Auktion — die Nachfrage wird über Inhalte aufgebaut
              statt gekauft. Die Felder tragen PUBLIVOs eigene Namen, damit die
              Säule direkt übernommen werden kann. */}
          {!!plan.publivo_themen?.length && (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                Themensäulen für PUBLIVO — organisch statt Anzeigen
              </p>
              <div className="space-y-2">
                {plan.publivo_themen.map((t, i) => (
                  <div key={i} className="rounded-lg border border-violet-200 bg-violet-50/40 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="flex flex-wrap items-baseline gap-1.5 text-sm font-medium text-gray-900">
                        {t.name}
                        {t.abgleich && SAEULEN_ABGLEICH[t.abgleich] && (
                          <span
                            className={`rounded border px-1.5 py-0.5 text-[10px] font-normal ${SAEULEN_ABGLEICH[t.abgleich].cls}`}
                            title={t.vorhandene_saeule ? `Bestehende Säule: ${t.vorhandene_saeule}` : undefined}
                          >
                            {SAEULEN_ABGLEICH[t.abgleich].label}
                          </span>
                        )}
                      </span>
                      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                        <PromptKnopf
                          text={saeuleAlsText(t)}
                          titel={`Themensäule — ${t.name}`}
                          beschriftung="Säule kopieren"
                        />
                        {(() => {
                          const stand = saeuleStatus[t.name] || t.uebernommen;
                          // Existiert die Säule bereits deckungsgleich, gibt es
                          // nichts zu übertragen — ein Knopf würde nur eine
                          // Dublette oder ein sinnloses Update anbieten.
                          if (t.abgleich === 'deckt_sich' && !stand) {
                            return (
                              <span className="text-[11px] text-gray-400">bereits in PUBLIVO abgedeckt</span>
                            );
                          }
                          return (
                            <button
                              onClick={() => saeuleUebergeben(t.name)}
                              disabled={saeuleLaeuft === t.name || !!stand}
                              title="In PUBLIVO übernehmen — vorhandene Säule wird nachgeschärft, eine neue nur nach Rückfrage"
                              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-60 ${
                                stand
                                  ? 'border border-emerald-300 text-emerald-700'
                                  : 'bg-violet-600 text-white hover:bg-violet-700'
                              }`}
                            >
                              {saeuleLaeuft === t.name
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : stand ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                              {stand === 'angelegt' ? 'In PUBLIVO angelegt'
                                : stand === 'aktualisiert' ? 'In PUBLIVO nachgeschärft'
                                : t.abgleich === 'nachschaerfen' ? 'Säule nachschärfen'
                                : 'Nach PUBLIVO'}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    {t.description && <p className="mt-1 text-xs text-gray-700">{t.description}</p>}
                    {t.warum_kein_ads && (
                      <p className="mt-1 text-[11px] text-gray-500">
                        <span className="text-gray-400">Warum organisch: </span>{t.warum_kein_ads}
                      </p>
                    )}
                    {!!t.example_topics?.length && (
                      <ul className="mt-1.5 space-y-0.5">
                        {t.example_topics.slice(0, 5).map((e, j) => (
                          <li key={j} className="text-[11px] text-gray-600">• {e}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Briefing für den Kunden — erst wenn die Entscheidung steht */}
          {!!plan.kampagnen?.length && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => briefingMut.mutate(false)}
                disabled={briefingMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {briefingMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Briefing für den Kunden
              </button>
            </div>
          )}

          {briefing !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4" onClick={() => setBriefing(null)}>
              <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Briefing für den Kunden</p>
                    <p className="text-[11px] text-gray-500">Wie die Website aufgebaut ist und was das Ziel ist — ohne Anrede und Grußformel.</p>
                  </div>
                  <button onClick={() => setBriefing(null)} aria-label="Schließen" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <textarea
                  value={briefing}
                  onChange={(e) => setBriefing(e.target.value)}
                  rows={11}
                  className="w-full resize-y border-0 px-5 py-4 text-sm leading-relaxed text-gray-800 outline-none"
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
                  <button
                    onClick={() => briefingMut.mutate(true)}
                    disabled={briefingMut.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {briefingMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    Neu formulieren
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(briefing);
                        setBriefingKopiert(true);
                        setTimeout(() => setBriefingKopiert(false), 2000);
                      } catch { toast.error('Kopieren nicht möglich'); }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
                  >
                    {briefingKopiert ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {briefingKopiert ? 'Kopiert' : 'Text kopieren'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Belege und Nebenbefunde, zugeklappt ──
              Die Einordnung ist der BEWEIS hinter der Entscheidung, nicht die
              Entscheidung selbst. Offen ausgebreitet macht sie die Seite so
              lang, dass man das Ergebnis nicht mehr findet. */}
          <details className="rounded-lg border border-gray-200">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
              Belege und Nebenbefunde
              <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                {[
                  plan.einordnung?.length ? `${plan.einordnung.length} Nachfrage-Gruppen` : null,
                  plan.negative_keywords?.length ? `${plan.negative_keywords.length} Ausschlüsse` : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </summary>

            <div className="space-y-4 border-t border-gray-100 px-3 py-3">
              {!!plan.einordnung?.length && (
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Wo die Nachfrage hingehört</p>
                  <div className="space-y-1.5">
                    {plan.einordnung.map((e, i) => (
                      <div key={i} className="rounded-lg border border-gray-200 p-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-gray-900">{e.cluster}</span>
                          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${KLASSE[e.klasse]?.cls || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                            {KLASSE[e.klasse]?.label || e.klasse}
                          </span>
                          {e.suchvolumen_monat != null && (
                            <span className="text-[11px] text-gray-500">{new Intl.NumberFormat('de-DE').format(e.suchvolumen_monat)}/Mon.</span>
                          )}
                          {e.klickpreis_eur && <span className="text-[11px] text-gray-400">{e.klickpreis_eur} €</span>}
                          {e.auftragswert && (
                            <span
                              className={`rounded border px-1.5 py-0.5 text-[10px] ${AUFTRAGSWERT[e.auftragswert] || ''}`}
                              title={e.auftrag_typisch}
                            >
                              Auftrag {e.auftragswert}
                            </span>
                          )}
                        </div>
                        {!!e.beispiel_begriffe?.length && (
                          <p className="mt-1 text-[11px] text-gray-500">{e.beispiel_begriffe.join(' · ')}</p>
                        )}
                        {e.beleg && <p className="mt-1 text-[11px] text-gray-600"><span className="text-gray-400">Beleg: </span>{e.beleg}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!plan.negative_keywords?.length && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Von Tag eins ausschließen</p>
                  <p className="text-[11px] leading-relaxed text-gray-600">{plan.negative_keywords.join(' · ')}</p>
                  <p className="mt-1.5 text-[10px] text-gray-400">
                    Werden beim Kampagnen-Entwurf im Google-Ads-Bereich automatisch mitgegeben — du musst sie nicht abtippen.
                  </p>
                </div>
              )}

              {!!plan.hinweise?.length && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  {plan.hinweise.map((h, i) => <p key={i} className="text-[11px] text-amber-800">• {h}</p>)}
                </div>
              )}
            </div>
          </details>
          {analyse?.verbrauch && Object.keys(analyse.verbrauch).length > 0 && (
            <p className="text-[10px] text-gray-400">
              Abgerufen: {Object.entries(analyse.verbrauch).map(([k, v]) => `${k} ×${v}`).join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Ein Auftrag für Claude Code — als Knopf, der ihn in einem Fenster zeigt.
 *
 * Bewusst NICHT auf der Seite ausgebreitet: Es gibt jetzt einen Auftrag je
 * Kampagne plus den allgemeinen. Vier dunkle Textblöcke untereinander waren
 * genau das, was die Seite unlesbar gemacht hat. Man braucht immer nur den
 * einen, an dem man gerade arbeitet.
 */
function PromptKnopf({ text, titel, beschriftung }: { text: string; titel: string; beschriftung: string }) {
  const [offen, setOffen] = useState(false);
  const [kopiert, setKopiert] = useState(false);

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch { toast.error('Kopieren nicht möglich'); }
  };

  return (
    <>
      <button
        onClick={() => setOffen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-800 bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
      >
        <Terminal className="h-3.5 w-3.5" /> {beschriftung}
      </button>

      {offen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4" onClick={() => setOffen(false)}>
          <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{titel}</p>
                <p className="text-[11px] text-gray-500">Im Repository der Kundenwebsite in Claude Code einfügen.</p>
              </div>
              <button onClick={() => setOffen(false)} aria-label="Schließen" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            <pre className="flex-1 overflow-y-auto whitespace-pre-wrap break-words bg-gray-900 px-5 py-4 text-[12px] leading-relaxed text-gray-100">
              {text}
            </pre>
            <div className="flex items-center justify-end border-t border-gray-100 px-5 py-3">
              <button
                onClick={kopieren}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
              >
                {kopiert ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {kopiert ? 'Kopiert' : 'Prompt kopieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
